import { useState } from 'react';
import { fmt } from '../../lib/nmtc/calculations';
import exportDealExcel from '../../lib/nmtc/exportDealExcel';

const SIZE_TIERS = [
  {
    label: 'Too Small',
    range: 'Under $3M',
    guidance: 'Rarely cost-effective — fixed closing costs (~$200K–$350K) consume too much of the benefit.',
    min: 0,
    max: 3_000_000,
    colorBg: 'bg-red-50',
    colorBorder: 'border-red-200',
    colorText: 'text-red-800',
    colorRing: 'ring-red-400',
    colorActiveBg: 'bg-red-100',
    colorActiveBorder: 'border-red-400',
    colorActiveText: 'text-red-900',
  },
  {
    label: 'Possible',
    range: '$3M – $7M',
    guidance: 'Feasible, but CDEs are selective at this size. A strong community impact story is essential.',
    min: 3_000_000,
    max: 7_000_000,
    colorBg: 'bg-amber-50',
    colorBorder: 'border-amber-200',
    colorText: 'text-amber-800',
    colorRing: 'ring-amber-400',
    colorActiveBg: 'bg-amber-100',
    colorActiveBorder: 'border-amber-400',
    colorActiveText: 'text-amber-900',
  },
  {
    label: 'Sweet Spot',
    range: '$7M – $15M',
    guidance: 'The sweet spot for most CDEs. Highest acceptance rate.',
    min: 7_000_000,
    max: 15_000_000,
    colorBg: 'bg-emerald-50',
    colorBorder: 'border-emerald-200',
    colorText: 'text-emerald-800',
    colorRing: 'ring-emerald-500',
    colorActiveBg: 'bg-emerald-100',
    colorActiveBorder: 'border-emerald-500',
    colorActiveText: 'text-emerald-900',
  },
  {
    label: 'Competitive',
    range: '$15M – $30M',
    guidance: 'Large enough that multiple CDEs compete for the deal. Often structured with sub-CDEs.',
    min: 15_000_000,
    max: 30_000_000,
    colorBg: 'bg-blue-50',
    colorBorder: 'border-blue-200',
    colorText: 'text-blue-800',
    colorRing: 'ring-blue-400',
    colorActiveBg: 'bg-blue-100',
    colorActiveBorder: 'border-blue-400',
    colorActiveText: 'text-blue-900',
  },
  {
    label: 'Multi-CDE',
    range: '$30M+',
    guidance: 'Typically requires multiple CDE allocations or a large national CDE.',
    min: 30_000_000,
    max: null,
    colorBg: 'bg-purple-50',
    colorBorder: 'border-purple-200',
    colorText: 'text-purple-800',
    colorRing: 'ring-purple-400',
    colorActiveBg: 'bg-purple-100',
    colorActiveBorder: 'border-purple-400',
    colorActiveText: 'text-purple-900',
  },
];

function activeTierIndex(financing) {
  for (let i = 0; i < SIZE_TIERS.length; i++) {
    const tier = SIZE_TIERS[i];
    if (tier.max === null) return i;
    if (financing < tier.max) return i;
  }
  return SIZE_TIERS.length - 1;
}

const PROHIBITED = [
  'Golf Course / Country Club',
  'Massage Parlor / Hot Tub Facility',
  'Racetrack / Gambling Facility',
  'Liquor Store (alcohol as primary business)',
  'Tanning / Suntan Facility',
];

const CONSIDERATIONS = [
  {
    icon: '⏱',
    title: '6–18 Month Timeline',
    body: 'NMTC transactions are complex. From initial discussions to closing typically takes 6–18 months. Plan your project timeline accordingly — NMTC is not bridge financing.',
  },
  {
    icon: '🔒',
    title: '7-Year Lock-In',
    body: "The structure must stay in place for 7 years. You can't prepay the notes, sell the project, or materially change the business without CDE approval during the compliance period.",
  },
  {
    icon: '📋',
    title: 'Annual Compliance',
    body: "You'll need to file annual reports demonstrating QALICB status — the gross income, property, and employee services tests. Budget for ongoing compliance support.",
  },
  {
    icon: '⚖️',
    title: 'Year 7 Tax Planning',
    body: 'B-Note forgiveness can create cancellation-of-debt income for for-profit borrowers. Engage a tax advisor experienced in NMTC exits well before Year 7.',
  },
  {
    icon: '🏢',
    title: 'You Work Through a CDE',
    body: "You don't apply to the CDFI Fund directly — you apply through a certified CDE that holds NMTC allocation. CDEs serve as the intermediary between investors and your project.",
  },
  {
    icon: '📊',
    title: 'Allocation Is Limited',
    body: "The CDFI Fund allocates credits annually through a competitive process. CDEs don't always have allocation available. Start conversations early.",
  },
  {
    icon: '📅',
    title: 'Know the Allocation Cycle',
    body: 'The CDFI Fund typically announces NMTC awards in the fall, though timing varies by round. CDEs must issue all investments within the timeline set by their Allocation Agreement (typically 5 years). Ask any CDE whether they currently have available allocation.',
  },
  {
    icon: '📬',
    title: 'Contact Multiple CDEs',
    body: "Don't approach one CDE at a time — simultaneously contact 3–5 CDEs whose geographic focus and sector preferences match your project. CDEs have strong preferences (e.g., a healthcare-focused CDE won't do a manufacturing deal).",
  },
];

const TIMELINE_STEPS = [
  { label: 'Initial CDE conversations', duration: '1–3 months' },
  { label: 'CDE due diligence + Letter of Intent', duration: '2–4 months' },
  { label: 'Investor syndication — CDE finds tax credit investor', duration: '1–3 months' },
  { label: 'Document drafting and negotiation', duration: '2–4 months' },
  { label: 'Closing', duration: '1 month' },
];

function SectionHeading({ title }) {
  return (
    <div className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
      <div className="w-16 h-1 bg-emerald-700 rounded" />
    </div>
  );
}

function LocationIcon({ status }) {
  if (status === true) {
    return (
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-bold text-lg">
        ✓
      </span>
    );
  }
  if (status === false) {
    return (
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 font-bold text-lg">
        ✕
      </span>
    );
  }
  return (
    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-400 font-bold text-lg">
      ?
    </span>
  );
}

function locationStatusClass(status) {
  if (status === true) return 'text-sm font-medium mb-4 text-emerald-700';
  if (status === false) return 'text-sm font-medium mb-4 text-red-600';
  return 'text-sm font-medium mb-4 text-gray-500';
}

function locationStatusText(status) {
  if (status === true) return 'Your project location appears eligible.';
  if (status === false) return 'Your project location does not appear to qualify.';
  return 'Location eligibility not yet confirmed.';
}

function EmailModal({ onClose, onSubmit, submitting }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({ name, email, organization: org });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Download Your Deal Analysis</h3>
          <p className="text-gray-500 text-sm mt-1">
            Enter your details to receive a branded Excel workbook with your full NMTC deal model.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-netlify="true" name="nmtc-download">
          <input type="hidden" name="form-name" value="nmtc-download" />
          <div>
            <label htmlFor="dl-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              id="dl-name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label htmlFor="dl-email" className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
            <input
              id="dl-email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label htmlFor="dl-org" className="block text-sm font-medium text-gray-700 mb-1">Organization <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              id="dl-org"
              name="organization"
              type="text"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              placeholder="Company or organization name"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white px-5 py-3 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Preparing download…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Excel
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 text-center">
            We respect your privacy. No spam — just NMTC insights.
          </p>
        </form>
      </div>
    </div>
  );
}

export default function HowToStartTab({ locationQualifies, businessType, isNonProfit, qei, calc, inputs }) {
  const activeIdx = activeTierIndex(qei);
  const isProhibited = PROHIBITED.includes(businessType);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleEmailSubmit({ name, email, organization }) {
    setSubmitting(true);
    try {
      // Submit to Netlify Forms
      const formData = new URLSearchParams();
      formData.append('form-name', 'nmtc-download');
      formData.append('name', name);
      formData.append('email', email);
      formData.append('organization', organization);
      formData.append('qei', String(calc.qei));
      formData.append('downloaded_at', new Date().toISOString());

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      // Track in GA4
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          currency: 'USD',
          value: calc.qei,
          lead_source: 'nmtc_excel_download',
        });
      }

      // Trigger the download
      exportDealExcel(inputs, calc);
      setShowModal(false);
    } catch {
      // If form submission fails, still allow the download
      exportDealExcel(inputs, calc);
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-0">

      {showModal && (
        <EmailModal
          onClose={() => setShowModal(false)}
          onSubmit={handleEmailSubmit}
          submitting={submitting}
        />
      )}

      {/* ── Section 1: Deal Size Guidance */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading title="Is NMTC Right for Your Project Size?" />

          <p className="text-gray-600 text-sm mb-8">
            Your modeled financing:{' '}
            <span className="font-semibold text-gray-900">{fmt(qei)}</span>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            {SIZE_TIERS.map((tier, i) => {
              const isActive = i === activeIdx;
              return (
                <div
                  key={tier.label}
                  className={[
                    'rounded-xl p-4 border-2 transition-all',
                    isActive
                      ? `${tier.colorActiveBg} ${tier.colorActiveBorder} ring-2 ${tier.colorRing} ring-offset-1 shadow-md`
                      : `${tier.colorBg} ${tier.colorBorder}`,
                  ].join(' ')}
                >
                  <div className="flex sm:flex-col items-start sm:items-start gap-3 sm:gap-1">
                    <div>
                      <p className={['font-bold text-sm', isActive ? tier.colorActiveText : tier.colorText].join(' ')}>
                        {tier.label}
                      </p>
                      <p className={['text-xs font-medium mt-0.5', isActive ? tier.colorActiveText : tier.colorText].join(' ')}>
                        {tier.range}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed sm:mt-2">{tier.guidance}</p>
                  </div>
                  {isActive && (
                    <p className="mt-3 text-xs font-semibold text-gray-700 flex items-center gap-1">
                      <span>Your project</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Section 2: Census Tract / Location Eligibility */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <SectionHeading title="Is Your Project Location Eligible?" />

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-emerald-900 mb-3">
              Low-Income Community (LIC) Census Tract Requirements
            </h3>
            <p className="text-gray-700 text-sm mb-3">
              Your project must be located in a census tract that qualifies as a Low-Income
              Community under at least one of the following criteria:
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-700 text-white text-xs flex items-center justify-center font-bold">
                  A
                </span>
                <span>
                  <strong>Poverty rate ≥ 20%</strong> — the census tract poverty rate equals or
                  exceeds 20% based on the most recent census data.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-700 text-white text-xs flex items-center justify-center font-bold">
                  B
                </span>
                <span>
                  <strong>Median family income &lt; 80% of area median</strong> — the tract's
                  median family income (MFI) is below 80% of the greater metropolitan or statewide MFI.
                </span>
              </li>
            </ul>
            <p className="mt-4 text-xs text-gray-500 italic">
              More than 40% of all U.S. census tracts qualify as Low-Income Communities under the NMTC program.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Check Your Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Option A — Recommended</p>
                  <p className="text-gray-500 text-sm mt-1">
                    The official CDFI Fund NMTC eligibility mapper. Most authoritative source for confirming LIC status.
                  </p>
                </div>
                <a
                  href="https://www.cdfifund.gov/cims"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg font-semibold text-sm text-center transition-colors"
                >
                  CDFI Fund NMTC Mapper
                </a>
              </div>
              <div className="border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Option B — Alternative</p>
                  <p className="text-gray-500 text-sm mt-1">
                    HUD&rsquo;s LIHTC mapper also shows census tract income data and can serve as a secondary reference.
                  </p>
                </div>
                <a
                  href="https://lihtc.huduser.gov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block border border-emerald-700 text-emerald-700 hover:bg-emerald-50 px-6 py-3 rounded-lg font-semibold text-sm text-center transition-colors"
                >
                  HUD LIHTC Mapper
                </a>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">How to Check Your Address</h3>
            <ol className="space-y-3">
              {[
                <>Visit the <a href="https://www.cdfifund.gov/cims" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2">CDFI Fund&rsquo;s NMTC eligibility mapper</a>.</>,
                'Search your project address in the mapper tool.',
                'Confirm the census tract shows as "Low-Income Community."',
                "Save the census tract number — you'll need it for the CDE application.",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="mt-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── Section 3: Eligibility Checklist */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading title="Are You Eligible?" />

          {isProhibited && (
            <div className="mb-6 bg-red-50 border border-red-300 rounded-xl px-5 py-4 flex items-start gap-3">
              <span className="text-red-500 text-xl flex-shrink-0 mt-0.5">✕</span>
              <div>
                <p className="font-semibold text-red-700 text-sm">Prohibited Business Type</p>
                <p className="text-red-600 text-sm mt-0.5">
                  <strong>{businessType}</strong> is a prohibited business type under the NMTC program. This project is ineligible regardless of location.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Location Check */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <LocationIcon status={locationQualifies} />
                <h3 className="font-semibold text-gray-900">Location Check</h3>
              </div>
              <p className={locationStatusClass(locationQualifies)}>
                {locationStatusText(locationQualifies)}
              </p>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                Qualifying criteria (either one):
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                  Poverty rate ≥ 20% in the census tract
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                  Median family income &lt; 80% of area median income
                </li>
              </ul>
              <a
                href="https://www.cdfifund.gov/cims"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-xs text-emerald-700 hover:text-emerald-900 underline underline-offset-2 transition-colors"
              >
                Check on the CDFI Fund mapper →
              </a>
            </div>

            {/* QALICB Requirements */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold text-sm">
                  Q
                </span>
                <h3 className="font-semibold text-gray-900">QALICB Requirements</h3>
              </div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">
                All three tests required:
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5 text-gray-400 text-base leading-none">○</span>
                  <span><strong>Gross income test</strong> — ≥ 50% of gross income derived from active business in a Low-Income Community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5 text-gray-400 text-base leading-none">○</span>
                  <span><strong>Tangible property test</strong> — ≥ 40% of tangible property used in the Low-Income Community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5 text-gray-400 text-base leading-none">○</span>
                  <span><strong>Employee services test</strong> — ≥ 40% of employee services performed in the Low-Income Community</span>
                </li>
              </ul>
              <p className="mt-4 text-xs text-gray-400 italic">
                These are self-certified annually by the borrower and confirmed during CDE due diligence.
              </p>
            </div>

            {/* What to Prepare */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm">
                  ✓
                </span>
                <h3 className="font-semibold text-gray-900">What to Prepare</h3>
              </div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">
                Documents you&rsquo;ll need:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                {[
                  'Project narrative and business plan',
                  '3 years of financial statements (audited preferred)',
                  'Detailed project budget / uses of funds',
                  'Census tract eligibility confirmation',
                  'Community impact narrative (jobs, services)',
                  'Construction timeline (if applicable)',
                  'Other committed financing in the capital stack',
                  'Organizational documents',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="flex-shrink-0 text-amber-600 font-semibold">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {isNonProfit === false && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 text-amber-600 text-xl mt-0.5">⚠</span>
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">For-Profit Entity</h3>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    As a for-profit entity, NMTC is still fully available, but plan for cancellation-of-debt income when the B-Note is forgiven at Year 7. See the Unwind tab for tax planning strategies.
                  </p>
                </div>
              </div>
            </div>
          )}
          {isNonProfit === true && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 text-emerald-600 text-xl mt-0.5">✓</span>
                <div>
                  <h3 className="font-semibold text-emerald-900 mb-1">Non-Profit Organization</h3>
                  <p className="text-emerald-800 text-sm leading-relaxed">
                    Non-profit organizations generally face a simpler NMTC path — no COD income at unwind, and many CDEs have a preference for non-profit community service organizations.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Section 4: Key Considerations */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHeading title="What to Know Before You Pursue NMTC" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {CONSIDERATIONS.map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 5: Timeline Expectations */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <SectionHeading title="Timeline Expectations" />

          <div className="relative">
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-emerald-200" aria-hidden="true" />
            <ol className="space-y-6">
              {TIMELINE_STEPS.map((step, i) => (
                <li key={step.label} className="flex items-start gap-5 pl-2">
                  <span className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                    {i + 1}
                  </span>
                  <div className="pt-1">
                    <p className="font-semibold text-gray-900 text-sm">{step.label}</p>
                    <p className="text-emerald-700 text-xs font-medium mt-0.5">{step.duration}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <p className="mt-8 text-sm text-gray-600 border-t border-gray-200 pt-5">
            <span className="font-semibold text-gray-900">Total:</span> typically 6–15 months from first contact to closing.
          </p>
        </div>
      </section>

      {/* ── Section 6: CTA */}
      <section className="py-16 px-4 bg-emerald-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-white mb-2">Ready to Take the Next Step?</h2>
            <div className="w-16 h-1 bg-emerald-400 rounded" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Download Deal Summary</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Export your full deal analysis as a branded Excel workbook — ready for your team or CDE conversations.
                </p>
              </div>
              <div className="mt-auto">
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 rounded-lg font-semibold text-sm text-center transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Excel
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Talk to a CDE</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Contact Clarity Impact Finance to discuss whether your project qualifies and whether allocation is currently available.
                </p>
              </div>
              <div className="mt-auto">
                <a
                  href="https://clarityimpactfinance.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 rounded-lg font-semibold text-sm text-center transition-colors"
                >
                  Schedule a Consultation →
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Find Additional CDEs</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Contact 3–5 CDEs simultaneously. Use the CDFI Fund&rsquo;s CDE directory to find CDEs active in your geography and sector.
                </p>
              </div>
              <div className="mt-auto">
                <a
                  href="https://www.cdfifund.gov/programs-training/programs/new-markets-tax-credit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full border border-emerald-700 text-emerald-700 hover:bg-emerald-50 px-5 py-3 rounded-lg font-semibold text-sm text-center transition-colors"
                >
                  CDFI Fund CDE Directory →
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 flex flex-col gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Run Your Numbers</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Use the model above to refine your scenario before your first CDE conversation.
                </p>
              </div>
              <div className="mt-auto">
                <a
                  href="#top"
                  className="inline-block w-full border border-gray-300 text-gray-700 hover:bg-gray-50 px-5 py-3 rounded-lg font-semibold text-sm text-center transition-colors"
                >
                  Back to the Model ↑
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
